import {
  Alert,
  Button,
  Card,
  Field,
  Input,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { toaster } from '@mono/chakra';
import { FC, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useCurrentOrganization, useOrganizationSettings } from './hooks';
import { canManageOrganizationSettings } from './roles';

type FormValues = {
  name: string;
};

const OrganizationsSettingsTab: FC = () => {
  const currentOrganization = useCurrentOrganization();
  const canManageSettings = canManageOrganizationSettings(
    currentOrganization?.role
  );
  const {
    organization,
    isLoading,
    loadError,
    refresh,
    updateOrganization,
    isSaving,
    saveError,
  } = useOrganizationSettings();

  const {
    register,
    handleSubmit,
    reset,
    formState,
  } = useForm<FormValues>({
    defaultValues: {
      name: '',
    },
  });

  useEffect(() => {
    if (organization) {
      reset({ name: organization.name });
    }
  }, [organization, reset]);

  const onSubmit: SubmitHandler<FormValues> = async ({ name }) => {
    if (!canManageSettings) {
      return;
    }

    try {
      const updated = await updateOrganization({ name });
      reset({ name: updated.name });
      toaster.create({
        type: 'success',
        title: 'Changes saved',
        description: 'Organization details updated successfully.',
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Unable to update organization.';

      toaster.create({
        type: 'error',
        title: 'Update failed',
        description: message,
      });
    }
  };

  const submitting = formState.isSubmitting || isSaving;
  const canSave = formState.isDirty && !submitting;

  return (
    <Stack gap="6">
      <Card.Root>
        <Card.Header gap="1">
          <Card.Title>Organization details</Card.Title>
          <Card.Description>
            Update how your workspace appears to your teammates.
          </Card.Description>
        </Card.Header>
        <Card.Body>
          {isLoading ? (
            <Stack align="center" gap="3" py="6">
              <Spinner />
              <Text fontSize="sm" color="fg.muted">
                Loading organization…
              </Text>
            </Stack>
          ) : loadError ? (
            <Stack gap="4">
              <Alert.Root status="error">
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Title>Unable to load organization</Alert.Title>
                  <Alert.Description>{loadError}</Alert.Description>
                </Alert.Content>
              </Alert.Root>
              <Button onClick={() => void refresh()} alignSelf="flex-start">
                Try again
              </Button>
            </Stack>
          ) : organization ? (
            <Stack
              as="form"
              id="organization-settings-form"
              gap="4"
              onSubmit={handleSubmit(onSubmit)}
            >
              <Field.Root invalid={Boolean(formState.errors.name)}>
                <Field.Label>Organization name</Field.Label>
                <Input
                  placeholder="Acme Inc."
                  disabled={!canManageSettings}
                  {...register('name', {
                    required: 'Organization name is required.',
                    minLength: {
                      value: 2,
                      message: 'Name must contain at least 2 characters.',
                    },
                  })}
                />
                <Field.HelperText>
                  This name is visible to members and appears in navigation.
                </Field.HelperText>
                <Field.ErrorText>
                  {formState.errors.name?.message}
                </Field.ErrorText>
                {!canManageSettings ? (
                  <Text color="fg.muted" fontSize="sm">
                    You need Maintainer access to edit organization details.
                  </Text>
                ) : null}
              </Field.Root>
              {saveError ? (
                <Text color="fg.error" fontSize="sm">
                  {saveError}
                </Text>
              ) : null}
            </Stack>
          ) : (
            <Alert.Root status="warning">
              <Alert.Indicator />
              <Alert.Content>
                <Alert.Title>No organization selected</Alert.Title>
                <Alert.Description>
                  Choose an organization to manage its settings.
                </Alert.Description>
              </Alert.Content>
            </Alert.Root>
          )}
        </Card.Body>
        {!isLoading && !loadError && organization ? (
          <Card.Footer gap="3" justifyContent={"flex-end"}>
            <Button
              variant="ghost"
              onClick={() =>
                reset({ name: organization.name }, { keepDirty: false })
              }
              disabled={submitting || !formState.isDirty || !canManageSettings}
            >
              Reset
            </Button>
            <Button
              type="submit"
              form="organization-settings-form"
              loading={submitting}
              disabled={!canSave || !canManageSettings}
              colorPalette="primary"
            >
              Save changes
            </Button>
          </Card.Footer>
        ) : null}
      </Card.Root>
    </Stack>
  );
};

export default OrganizationsSettingsTab;
